<template>
  <div class="dept-item-wrapper">
    <div
      class="dept-item"
      :class="{ hasChild, expanded: isExpanded, selected: isSelected, leaf: isLeafDept }"
      @click="emit('select', node.deptId)"
    >
      <i
        v-if="hasChild"
        class="icon icon-sm dept-toggle-icon"
        :class="isExpanded ? 'icon-chevron-down' : 'icon-chevron-right'"
        @click.stop="emit('toggle', node.deptId)"
      ></i>
      <span class="dept-name">{{ isLeafDept ? '- ' : '' }}{{ deptName }}</span>
    </div>

    <div class="sub-departments" v-if="hasChild && isExpanded">
      <OrganizationTreeItem
        v-for="child in children"
        :key="getFullDeptId(child) || child.deptId"
        :item="child"
        :list="list"
        :selected-id="selectedId"
        :expanded-ids="expandedIds"
        @select="$emit('select', $event)"
        @toggle="$emit('toggle', $event)"
      />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import OrganizationTreeItem from './OrganizationTreeItem.vue';

const props = defineProps({
  item: { type: Object, required: true },
  list: { type: Array, required: true },
  selectedId: [String, Number],
  expandedIds: Array
});

const emit = defineEmits(['select', 'toggle']);

const node = computed(() => props.item);

// fullDeptId(전체 경로) 필드를 방어적으로 읽는다. (예: '/D000001/D000198/D000206')
const getFullDeptId = d => String(d?.fullDeptId ?? d?.full_dept_id ?? d?.fullDeptPath ?? '');

// 부서명 필드도 방어적으로 읽는다. (deptNm 우선, 기존 deptFNm 대비)
const deptName = computed(() => node.value?.deptNm ?? node.value?.deptFNm ?? '');

// upDeptId가 같아도 deptId가 여러 브랜치에 중복될 수 있으므로,
// fullDeptId 경로가 부모의 fullDeptId로 시작하는 경우에만 자식으로 붙인다.
const children = computed(() => {
  const parentFull = getFullDeptId(node.value);
  return props.list.filter(d => {
    if (d.upDeptId !== node.value.deptId) return false;
    const childFull = getFullDeptId(d);
    if (parentFull && childFull) {
      return childFull.startsWith(`${parentFull}/`);
    }
    return true;
  });
});

const isLeafDept = computed(() => String(node.value?.isLeaf ?? '') === 'Y');
const hasChild = computed(() => {
  if (node.value?.isLeaf != null) {
    return String(node.value.isLeaf) === 'N';
  }
  return children.value.length > 0;
});
const isExpanded = computed(() => props.expandedIds?.includes(node.value.deptId));
const isSelected = computed(() => props.selectedId === node.value.deptId);
</script>

<style scoped>
.dept-item-wrapper {
  margin-bottom: 4px;
}
.dept-item {
  display: flex;
  align-items: center;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}
.dept-item.selected {
  background-color: rgba(255, 107, 53, 0.15);
}
.dept-item:hover {
  background-color: #f0f0f0;
}
.dept-item.hasChild {
  font-weight: 500;
}
.dept-toggle-icon {
  margin-right: 8px;
  color: #666;
  cursor: pointer;
}
.sub-departments {
  margin-left: 24px;
  margin-top: 4px;
}
</style>
